/**
 * Kaçış bot = solver.
 *
 *  - `routeOut`: BFS over one worm's configurations (forward moves of the grabbed end only) to the
 *    shortest drag that ends in a same-colour gate; everything else stays put.
 *  - `solveGreedy`: repeatedly escape the cheapest escapable worm. Escaping only ever frees cells and
 *    dissolves stones, so on levels without "sidestep" puzzles this never gets stuck.
 *  - `level.solution`: the constructive word-order witness the generator built by reverse construction
 *    (pull worms in through their gates, then scramble with reversible gestures), compressed by
 *    `compressWitness` (drop every sidestep gesture the run survives without). It covers scrambled levels
 *    and is the ★ Kelime path.
 *  - `noisyRun` / `grade`: a human-like bot that follows the cheapest plan but wastes moves (random
 *    exploratory drags, undone or not). The win rate within the move budget decides the difficulty band
 *    (easy ≥ .95, medium ≥ .8, hard ≥ .5, expert [.3,.5), < .3 broken → regenerate).
 */
import { Rng } from "@enjoyment/core";
import { applyGesture, beginDrag, cloneState, createGame, dragStep, endDrag, FREE, invertGesture, probe, replay, solidAt, wordStar, type GameState } from "./game";
import { DX, DY, DIRS, type Band, type Dir, type End, type Gesture, type KacisLevel } from "./types";

export const NOISE = 0.15;
export const GRADE_RUNS = 30;

// ---------------------------------------------------------------- single-worm routing

/** Shortest drag (list of dirs, last one leaving through the gate) that gets `worm` out via `end`, or null. */
export function routeOut(s: GameState, wi: number, end: End, cap = 5000): Dir[] | null {
  const w = s.worms[wi];
  if (!w || !w.alive) return null;
  const { cols, rows } = s;
  const L = w.cells.length;
  const start = w.cells.map((c) => c.y * cols + c.x);
  const keyOf = (cfg: number[]): number => {
    let k = 0;
    for (let i = 0; i < cfg.length; i++) k = k * 128 + cfg[i]!;
    return k;
  };
  const cfgs: number[][] = [start];
  const parent: number[] = [-1];
  const via: number[] = [-1];
  const seen = new Set<number>([keyOf(start)]);
  for (let qi = 0; qi < cfgs.length && qi < cap; qi++) {
    const cfg = cfgs[qi]!;
    const lead = end === "head" ? cfg[0]! : cfg[L - 1]!;
    const lx = lead % cols;
    const ly = (lead - lx) / cols;
    for (const d of DIRS) {
      const nx = lx + DX[d];
      const ny = ly + DY[d];
      if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) {
        const gc = s.gateMap.get(lead * 4 + d);
        if (gc === w.color) {
          const path: Dir[] = [d];
          for (let i = qi; parent[i]! >= 0; i = parent[i]!) path.push(via[i] as Dir);
          return path.reverse();
        }
        continue;
      }
      const id = ny * cols + nx;
      if (solidAt(s, id)) continue;
      const o = s.occ[id]!;
      if (o !== FREE && o !== wi) continue;
      if (cfg.includes(id)) continue;
      const next = end === "head" ? [id, ...cfg.slice(0, L - 1)] : [...cfg.slice(1), id];
      const k = keyOf(next);
      if (seen.has(k)) continue;
      seen.add(k);
      cfgs.push(next);
      parent.push(qi);
      via.push(d);
    }
  }
  return null;
}

export interface Route {
  worm: number;
  end: End;
  steps: Dir[];
}

/** Cheapest escape for one worm over both ends. */
export function bestRoute(s: GameState, wi: number): Route | null {
  let best: Route | null = null;
  for (const end of ["head", "tail"] as const) {
    const steps = routeOut(s, wi, end);
    if (steps && (!best || steps.length < best.steps.length)) best = { worm: wi, end, steps };
  }
  return best;
}

/** Cheapest escape among worms accepted by `allow`. */
export function cheapestEscape(s: GameState, allow: (w: number) => boolean = () => true): Route | null {
  let best: Route | null = null;
  for (const w of s.worms) {
    if (!w.alive || !allow(w.id)) continue;
    const r = bestRoute(s, w.id);
    if (r && (!best || r.steps.length < best.steps.length)) best = r;
  }
  return best;
}

/** Number of worms that can leave right now (level opening). */
export function openings(s: GameState): number {
  let n = 0;
  for (const w of s.worms) if (w.alive && bestRoute(s, w.id)) n++;
  return n;
}

// ---------------------------------------------------------------- plans

export type Intent = { kind: "escape"; worm: number } | { kind: "move"; gesture: Gesture };

/** Split a gesture list into intents by replaying it: gestures that end a worm's life are escapes. */
export function intentsOf(level: KacisLevel, gestures: Gesture[]): Intent[] {
  const s = createGame(level, 1e9);
  const out: Intent[] = [];
  for (const g of gestures) {
    const alive = s.worms[g.worm]!.alive;
    applyGesture(s, g);
    if (alive && !s.worms[g.worm]!.alive) out.push({ kind: "escape", worm: g.worm });
    else out.push({ kind: "move", gesture: g });
  }
  return out;
}

export interface PlanRun {
  ok: boolean;
  state: GameState;
  gestures: Gesture[];
}

/** Execute intents on a fresh game: escapes take the cheapest route, moves are replayed exactly. */
export function runIntents(level: KacisLevel, intents: Intent[]): PlanRun {
  const s = createGame(level, 1e9);
  for (const it of intents) {
    if (it.kind === "move") {
      const r = applyGesture(s, it.gesture);
      if (!r.ok) return { ok: false, state: s, gestures: s.gestures };
    } else {
      const r = bestRoute(s, it.worm);
      if (!r) return { ok: false, state: s, gestures: s.gestures };
      applyGesture(s, r);
    }
  }
  return { ok: s.status === "won", state: s, gestures: s.gestures };
}

/** Drop every sidestep gesture the run survives without; return the executed (exact) gesture list. */
export function compressWitness(level: KacisLevel, raw: Gesture[]): Gesture[] | null {
  let intents = intentsOf(level, raw);
  let run = runIntents(level, intents);
  if (!run.ok) return null;
  for (let pass = 0; pass < 2; pass++) {
    let changed = false;
    for (let i = intents.length - 1; i >= 0; i--) {
      if (intents[i]!.kind !== "move") continue;
      const trial = intents.slice(0, i).concat(intents.slice(i + 1));
      const r = runIntents(level, trial);
      if (r.ok) {
        intents = trial;
        run = r;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return run.gestures.map((g) => ({ worm: g.worm, end: g.end, steps: g.steps.slice() }));
}

/** Greedy any-order solution (escape-only), or null when a sidestep is needed. */
export function solveGreedy(level: KacisLevel): Gesture[] | null {
  const s = createGame(level, 1e9);
  while (s.status === "playing") {
    const r = cheapestEscape(s);
    if (!r) return null;
    applyGesture(s, r);
  }
  return s.status === "won" ? s.gestures.map((g) => ({ ...g, steps: g.steps.slice() })) : null;
}

export interface Solve {
  solved: boolean;
  gestures: Gesture[];
  moves: number;
  /** Escape-only (no sidestep) solution found. */
  greedy: boolean;
  /** Word-order (★) solution length. */
  starMoves: number;
}

const solveCache = new WeakMap<KacisLevel, Solve>();

export function solve(level: KacisLevel): Solve {
  const hit = solveCache.get(level);
  if (hit) return hit;
  const star = level.solution.length ? replay(level, level.solution, 1e9) : null;
  const starOk = !!star && wordStar(star);
  const starMoves = starOk ? level.solution.length : Infinity;
  const g = solveGreedy(level);
  let out: Solve;
  if (g && g.length <= starMoves) out = { solved: true, gestures: g, moves: g.length, greedy: true, starMoves };
  else if (starOk) out = { solved: true, gestures: level.solution, moves: level.solution.length, greedy: false, starMoves };
  else if (g) out = { solved: true, gestures: g, moves: g.length, greedy: true, starMoves };
  else out = { solved: false, gestures: [], moves: 0, greedy: false, starMoves };
  solveCache.set(level, out);
  return out;
}

/** Plan from the *current* live state (autoplay): greedy escapes; from an untouched board, fall back to the witness. */
export function planFrom(s: GameState): Gesture[] | null {
  // Untouched board: play the word-order (★) solution so autoplay demos the full second layer.
  if (s.gestures.length === 0 && s.moves === 0 && s.level.solution.length) {
    const chk = replay(s.level, s.level.solution, 1e9);
    if (wordStar(chk)) return s.level.solution;
  }
  const c = cloneState(s);
  c.budget = 1e9;
  const out: Gesture[] = [];
  while (c.status === "playing") {
    const r = cheapestEscape(c);
    if (!r) break;
    out.push({ worm: r.worm, end: r.end, steps: r.steps });
    applyGesture(c, r);
  }
  if (c.status === "won") return out;
  return null;
}

// ---------------------------------------------------------------- human-like noise

function bestIntents(level: KacisLevel): Intent[] {
  const sv = solve(level);
  return intentsOf(level, sv.gestures);
}

const intentCache = new WeakMap<KacisLevel, Intent[]>();
function intentsFor(level: KacisLevel): Intent[] {
  let it = intentCache.get(level);
  if (!it) {
    it = bestIntents(level);
    intentCache.set(level, it);
  }
  return it;
}

/** A wasted drag: random worm/end, 1–3 random non-exit steps. Returns the applied gesture and its inverse. */
function blunder(s: GameState, rng: Rng): { g: Gesture; inv: Gesture | null } | null {
  const alive = s.worms.filter((w) => w.alive);
  if (!alive.length) return null;
  for (let tries = 0; tries < 6; tries++) {
    const w = alive[rng.int(alive.length)]!;
    const end: End = rng.int(2) === 0 ? "head" : "tail";
    const before = cloneState(s);
    const want = 1 + rng.int(3);
    if (!beginDrag(s, w.id, end)) return null;
    for (let i = 0; i < want; i++) {
      const opts = DIRS.filter((d) => {
        const p = probe(s, w.id, end, d);
        return p.ok && p.kind === "move";
      });
      if (!opts.length) break;
      dragStep(s, opts[rng.int(opts.length)]!);
    }
    const steps = s.gesture ? s.gesture.steps.slice() : [];
    endDrag(s);
    if (!steps.length) continue;
    const g: Gesture = { worm: w.id, end, steps };
    return { g, inv: invertGesture(before, g) };
  }
  return null;
}

export interface NoisyResult {
  won: boolean;
  moves: number;
}

/** One human-like run with `noise` probability of a wasted drag before each intent. Unlimited budget. */
export function noisyRun(level: KacisLevel, seed: number, noise = NOISE, budget = 1e9): NoisyResult {
  const intents = intentsFor(level);
  const s = createGame(level, budget);
  const rng = new Rng(seed);
  const pending: Gesture[] = []; // inverses of blunders still on the board (most recent last)
  const undoOne = (): boolean => {
    const inv = pending.pop();
    if (!inv) return false;
    return applyGesture(s, inv).ok;
  };
  for (const it of intents) {
    if (s.status !== "playing") break;
    if (rng.next() < noise) {
      const b = blunder(s, rng);
      if (b && b.inv) {
        if (rng.next() < 0.5) {
          applyGesture(s, b.inv);
        } else pending.push(b.inv);
      }
      if (s.status !== "playing") break;
    }
    for (let attempt = 0; attempt < 4 && s.status === "playing"; attempt++) {
      if (it.kind === "escape") {
        const r = bestRoute(s, it.worm);
        if (r) {
          applyGesture(s, r);
          break;
        }
      } else if (applyGesture(cloneState(s), it.gesture).ok) {
        applyGesture(s, it.gesture);
        break;
      }
      if (!undoOne()) return { won: false, moves: s.moves };
    }
  }
  return { won: s.status === "won", moves: s.moves };
}

export function bandOf(rate: number): Band {
  if (rate >= 0.95) return "easy";
  if (rate >= 0.8) return "medium";
  if (rate >= 0.5) return "hard";
  if (rate >= 0.3) return "expert";
  return "broken";
}

/** Move counts of `runs` noisy runs (Infinity = never solved). Budget-independent: a run wins iff moves <= budget. */
export function noisyMoves(level: KacisLevel, runs = GRADE_RUNS, noise = NOISE): number[] {
  const out: number[] = [];
  for (let i = 0; i < runs; i++) {
    const r = noisyRun(level, level.seed * 31 + i * 7919 + 1, noise);
    out.push(r.won ? r.moves : Infinity);
  }
  return out;
}

export interface Grade {
  winRate: number;
  band: Band;
  par: number;
  starPar: number;
}

/** README grading: bot win rate within the level's budget. */
export function grade(level: KacisLevel, runs = GRADE_RUNS): Grade {
  const sv = solve(level);
  if (!sv.solved) return { winRate: 0, band: "broken", par: 0, starPar: 0 };
  const moves = noisyMoves(level, runs);
  const winRate = moves.filter((m) => m <= level.budget).length / moves.length;
  return { winRate, band: bandOf(winRate), par: sv.moves, starPar: sv.starMoves };
}
